import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, Bug, CalendarPlus, Info, X } from "lucide-react";
import { MAX_GROOMING_BATCH_ITEMS } from "@ttc-spectree/toolbox-contract";
import { AdoReadErrorCard, type AdoReadQuery } from "@/components/grooming/ado-read-error-card";
import { BriefStateChip } from "@/components/grooming/brief-state-chip";
import { Chip, Segmented } from "@/components/grooming/grooming-chips";
import { QueueBadge, activeEntryByKey, safeQueueKey } from "@/components/grooming/queue-status-badge";
import { SessionPicker } from "@/components/grooming/session-picker";
import { AGENDA_LOCKED_TEXT } from "@/components/grooming/session-prepared";
import { groomingKeys, useAddToAgenda, useMoveAgendaItem, useRemoveFromAgenda } from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import type { GroomingBacklogItem, GroomingBacklogSort, GroomingBatch } from "@/lib/api/grooming";
import {
  BACKLOG_SORT_LABELS,
  BACKLOG_SORT_MENU,
  sortRows,
  useBacklogSort,
  whoDate,
} from "@/lib/grooming/backlog-sort";
import { agendaErrorMessage, groomingApiErrorCode } from "@/lib/grooming/error-messages";
import { meetingMonthDay } from "@/lib/grooming/meeting-dates";
import { briefStateOf } from "@/lib/grooming/plain-words";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * The Session screen's build step (epic PI-SPEC-SW-E280, feature
 * PI-SPEC-SW-1402; Claude Design "SpecTree Grooming Desktop", Session build).
 *
 * Left: the Ready to groom queue in the shared sort (default Newest; # stays the ADO position), each with a
 * checkbox that adds it to (or removes it from) the project's one shared
 * agenda (decision 3: any grooming user builds it). Right: the agenda in the
 * order the team will discuss it. Items that need information never appear
 * in the queue; their authors get questions from the item page.
 *
 * There is no other way to choose items: the upcoming-sprint, unestimated
 * and tag modes of E279's Session prep are gone from the UI (decision 6
 * keeps them in the API only).
 *
 * E323 (ER-1710, feature PI-SPEC-SW-1739): each agenda row also has Move
 * (the shared SessionPicker) to send it to another upcoming meeting, and the
 * agenda stays editable until its meeting has passed (`locked`), when it
 * turns read-only with a plain note.
 */

/**
 * The note under the agenda for people who prepare briefs (UX-PM-10: the
 * text follows the gates as built). `concurrency` is the run queue's "Run at
 * once" setting from the Queue page; null when there is no queue state (the
 * browser, or an app older than grooming contract 3). Pure; exported for tests.
 */
export function prepareNote(concurrency: number | null): string {
  const cap = String(MAX_GROOMING_BATCH_ITEMS);
  return concurrency === null
    ? `Briefs run for agenda items only, up to ${cap} per session.`
    : `Briefs run for agenda items only. Runs ${String(concurrency)} at a time (set on the Queue page), up to ${cap} per session.`;
}

/**
 * How long preparing takes and when to do it (frame C3; 01-roles.md §9.3, a
 * hint only): "Preparing takes about 4 minutes an item on this computer.
 * Ideally the business day before the meeting; earlier or the same morning
 * is fine too." Pure; exported for tests.
 */
export function prepareTimingNote(typicalItemSeconds: number): string {
  const minutes = Math.max(1, Math.round(typicalItemSeconds / 60));
  return `Preparing takes about ${String(minutes)} ${minutes === 1 ? "minute" : "minutes"} an item on this computer. Ideally the business day before the meeting; earlier or the same morning is fine too.`;
}

/**
 * Whether an item's latest brief is missing, out of date or still current.
 * Named apart from plain-words' `BriefState`, which also says whether a
 * current brief is posted.
 */
export type AgendaBriefState = "none" | "stale" | "current";

/** How many Ready to groom items the queue shows before "+ N more". */
export const QUEUE_SHOWN = MAX_GROOMING_BATCH_ITEMS;

/**
 * The agenda's brief state of a backlog row. `hasCurrentBrief` decides
 * "current": a row whose AI suggestion is not out of date but has no current
 * brief is one whose newest brief was disregarded (ER-1712), so it needs a
 * brief again and Prepare briefs counts it.
 */
export function agendaBriefStateOf(item: GroomingBacklogItem): AgendaBriefState {
  if (item.hasCurrentBrief) return "current";
  if (item.aiSuggestion?.stale) return "stale";
  return "none";
}

/** One row of the Ready to groom queue: the item and its place in the backlog. */
export interface QueueRow {
  item: GroomingBacklogItem;
  /** 1-based position in the whole backlog (ADO order), taken before any sorting. */
  rank: number;
}

/** The backlog's Ready to groom items, in the backlog's order, with their backlog position. */
export function readyQueue(backlogItems: readonly GroomingBacklogItem[]): QueueRow[] {
  const rows: QueueRow[] = [];
  backlogItems.forEach((item, index) => {
    if (item.stage === "ready-to-groom") rows.push({ item, rank: index + 1 });
  });
  return rows;
}

/** The words for the Ready to groom list's order, in the header line. */
export const QUEUE_SORT_WORDS: Readonly<Record<GroomingBacklogSort, string>> = {
  newest: "newest first",
  rank: "ADO order",
  oldest: "oldest first",
};

/**
 * The queue in the chosen sort (ER-1656). Ranks were assigned from the ADO-order
 * read by {@link readyQueue}, so they stay each item's ADO position. Never mutates.
 */
export function sortedReadyQueue(rows: readonly QueueRow[], sort: GroomingBacklogSort): QueueRow[] {
  if (sort === "rank") return [...rows];
  const sorted = sortRows(
    rows.map((row) => ({ row, createdDate: row.item.createdDate })),
    sort
  );
  return sorted.map((entry) => entry.row);
}

/** One agenda row: the batch item with its brief state. */
export interface AgendaEntry {
  itemId: string;
  title: string;
  kind: "bug" | "story" | null;
  brief: AgendaBriefState;
  /** The latest brief's ADO comment status (held, posted, …), or null when there is none. */
  commentStatus: string | null;
  /** "Chris Patel · Oct 22" from the backlog row; null when the item is not in the backlog read. */
  whoDate: string | null;
}

/**
 * The agenda's items in discussion order (batch position) with their brief
 * state. The backlog row decides the brief state when the item is in the
 * backlog read; otherwise a briefed run item, or a draft item with a stored
 * brief, counts as current.
 */
export function agendaEntries(
  agenda: GroomingBatch | null,
  backlogById: ReadonlyMap<string, GroomingBacklogItem>
): AgendaEntry[] {
  if (!agenda) return [];
  return [...agenda.items]
    .sort((a, b) => a.position - b.position)
    .map((item) => {
      const row = backlogById.get(item.itemId);
      let brief: AgendaBriefState;
      if (row) brief = agendaBriefStateOf(row);
      else if (item.status === "briefed" || item.status === "current" || item.brief !== null) brief = "current";
      else brief = "none";
      return {
        itemId: item.itemId,
        title: row?.title ?? item.title,
        kind: row ? (row.kind === "bug" ? "bug" : "story") : null,
        brief,
        commentStatus: row?.aiSuggestion?.commentStatus ?? item.brief?.commentStatus ?? null,
        whoDate: row ? whoDate(row) : null,
      };
    });
}

/** The agenda's counts: items, with a current brief, and needing one (Prepare N briefs). */
export function agendaCounts(entries: readonly AgendaEntry[]): { items: number; briefed: number; needBrief: number } {
  const briefed = entries.filter((entry) => entry.brief === "current").length;
  return { items: entries.length, briefed, needBrief: entries.length - briefed };
}

/**
 * An item's brief in the shared words (UX-PM-09): a current brief says
 * whether it is posted, an out-of-date one says "Brief out of date" (never
 * "Needs brief"), and an item with no brief says "No brief".
 */
function BriefChip({
  state,
  commentStatus,
  testId,
}: {
  state: AgendaBriefState;
  commentStatus: string | null;
  testId: string;
}) {
  if (state === "none") {
    return (
      <Chip tone="none" testId={testId} dataState="none">
        No brief
      </Chip>
    );
  }
  return (
    <BriefStateChip
      testId={testId}
      state={state === "stale" ? "out-of-date" : briefStateOf({ commentStatus: commentStatus ?? "held", stale: false })}
    />
  );
}

function KindIcon({ kind }: { kind: "bug" | "story" | null }) {
  if (kind === "bug") return <Bug className="h-[13px] w-[13px] text-[#FF6B81]" aria-label="Bug" />;
  if (kind === "story") return <BookOpen className="h-[13px] w-[13px] text-[#6CC4FF]" aria-label="Story" />;
  return <span className="h-[13px] w-[13px]" aria-hidden="true" />;
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-[200px] flex-1 items-center justify-center" role="status" aria-live="polite">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export interface SessionBuildProps {
  project: string;
  /** The backlog read (whole backlog, backlog priority order); undefined while loading. */
  backlogItems: readonly GroomingBacklogItem[] | undefined;
  backlogLoading: boolean;
  backlogError: boolean;
  /** The backlog read itself, for the B6 error card's cause, Try again and last-tried time (UX-PM-37). */
  backlogQuery: AdoReadQuery;
  /** The agenda shown: the shared draft agenda, else the current run's agenda, else null. */
  agenda: GroomingBatch | null;
  /** Its rows, from {@link agendaEntries}. */
  entries: readonly AgendaEntry[];
  /**
   * `manage-agenda` on the project (Admin, Project Manager; E283): ticking
   * and removing agenda items. Without it the agenda is read-only.
   */
  canManageAgenda: boolean;
  /** `prepare-sessions` on the project (Admin, Project Manager): Prepare briefs. */
  canPrepare: boolean;
  /**
   * The agenda's meeting has passed (E323, `isPastMeeting`): the agenda is a
   * record, so nothing can be added, removed or moved (the API answers 409
   * GROOMING_AGENDA_LOCKED).
   */
  locked?: boolean;
  /** Words for the session in toasts, such as "Thursday's session". */
  sessionWords: string;
  /**
   * Median recent brief run in seconds (the project session's
   * `typicalItemSeconds`), for "Preparing takes about N minutes an item on
   * this computer" (frame C3); null or absent without history.
   */
  typicalItemSeconds?: number | null;
}

/** The item's run-queue badge while it is queued or running on this computer (E284), else `children`. */
function AgendaQueueOr({ project, itemId, children }: { project: string; itemId: string; children: ReactNode }) {
  const { state } = useGroomingQueue();
  const active = activeEntryByKey(state, safeQueueKey("groom-item", project, itemId));
  return active ? <QueueBadge status={active} /> : <>{children}</>;
}

/**
 * The agenda's "Briefs run for agenda items only…" note. It alone subscribes to
 * the run queue for the concurrency, so a queue event re-renders this line, not
 * the whole build step.
 */
function AgendaPrepareNote() {
  const { state } = useGroomingQueue();
  return <span data-testid="agenda-prepare-note">{prepareNote(state?.concurrency ?? null)}</span>;
}

/** The build step: the Ready to groom queue and the agenda side by side. */
export function SessionBuild({
  project,
  backlogItems,
  backlogLoading,
  backlogError,
  backlogQuery,
  agenda,
  entries,
  canManageAgenda,
  canPrepare,
  sessionWords,
  typicalItemSeconds = null,
  locked = false,
}: SessionBuildProps) {
  const queryClient = useQueryClient();
  const add = useAddToAgenda(project);
  const remove = useRemoveFromAgenda(project);
  const move = useMoveAgendaItem(project);
  const [showAll, setShowAll] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Without manage-agenda the agenda is read-only (the API would refuse the change). E323: an agenda in
  // any status but canceled changes until its meeting has passed.
  const editable = canManageAgenda && agenda !== null && agenda.status !== "canceled" && !locked;
  // Adding needs no agenda yet (the first add starts it), only manage-agenda and a meeting not passed.
  const canAdd = canManageAgenda && !locked;
  const inAgenda = new Set(agenda?.items.map((item) => item.itemId) ?? []);
  const atCap = editable && inAgenda.size >= MAX_GROOMING_BATCH_ITEMS;
  const [sort, setSort] = useBacklogSort();
  const queue = sortedReadyQueue(readyQueue(backlogItems ?? []), sort);
  const shown = showAll ? queue : queue.filter((row, index) => index < QUEUE_SHOWN || inAgenda.has(row.item.itemId));
  const more = queue.length - shown.length;
  const busy = add.isPending || remove.isPending || move.isPending;

  const failed = (cause: unknown) => {
    setPendingId(null);
    const code = groomingApiErrorCode(cause);
    setError(
      agendaErrorMessage(cause) ??
        (code === "GROOMING_BATCH_FULL"
          ? `An agenda holds at most ${String(MAX_GROOMING_BATCH_ITEMS)} items.`
          : "The agenda could not be changed. Try again in a moment.")
    );
    // Someone may have prepared the agenda meanwhile: read the session again.
    void queryClient.invalidateQueries({ queryKey: groomingKeys.session(project) });
  };

  const addItem = (itemId: string) => {
    setError(null);
    setPendingId(itemId);
    add.mutate([itemId], {
      onSuccess: () => {
        setPendingId(null);
        showGroomingToast(`Added to ${sessionWords}`);
      },
      onError: failed,
    });
  };

  const removeItem = (itemId: string) => {
    if (!agenda || !editable) return;
    setError(null);
    setPendingId(itemId);
    remove.mutate(
      { itemId, batchId: agenda.id },
      {
        onSuccess: () => {
          setPendingId(null);
          showGroomingToast("Removed from the session", "info");
        },
        onError: failed,
      }
    );
  };

  const moveItem = (itemId: string, toDate: string) => {
    if (!agenda || !editable) return;
    setError(null);
    setPendingId(itemId);
    move.mutate(
      { batchId: agenda.id, itemId, toDate },
      {
        onSuccess: () => {
          setPendingId(null);
          showGroomingToast(`Moved to the ${meetingMonthDay(toDate)} session`);
        },
        onError: failed,
      }
    );
  };

  const toggle = (itemId: string) => {
    if (!canAdd) return;
    if (inAgenda.has(itemId) && editable) removeItem(itemId);
    else if (!inAgenda.has(itemId)) addItem(itemId);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3.5 lg:flex-row" data-testid="session-build">
      {/* The Ready to groom queue */}
      <section
        className="glass-card flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden rounded-[10px]"
        aria-labelledby="ready-queue-heading"
        data-testid="ready-queue"
      >
        <div className="flex items-center gap-3 border-b border-[#1F1F23] px-4 py-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <h2 id="ready-queue-heading" className="font-display text-sm font-semibold">
              Ready to groom
            </h2>
            <span className="text-xs text-muted-foreground" data-testid="queue-subtitle">
              Meets the standard, not yet sized · {QUEUE_SORT_WORDS[sort]}
            </span>
          </div>
          <Segmented<GroomingBacklogSort>
            label="Sort"
            value={sort}
            onChange={setSort}
            disabled={backlogError || backlogLoading}
            options={BACKLOG_SORT_MENU.map((value) => ({ value, label: BACKLOG_SORT_LABELS[value] }))}
          />
        </div>

        {backlogError ? (
          <div className="p-4">
            <AdoReadErrorCard query={backlogQuery} subject="backlog" project={project} compact testId="session-backlog-error" />
          </div>
        ) : backlogLoading ? (
          <Spinner label="Reading the backlog from ADO…" />
        ) : queue.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground" data-testid="queue-empty">
            Nothing in the backlog is ready to groom right now.
          </p>
        ) : (
          <ul className="min-h-0 overflow-auto" aria-label="Ready to groom items">
            {shown.map(({ item, rank }) => {
              const on = inAgenda.has(item.itemId);
              const briefState = agendaBriefStateOf(item);
              const fixed = on && !editable;
              const disabled = !canAdd || busy || fixed || (!on && atCap);
              return (
                <li key={item.itemId} data-testid="queue-row" data-item-id={item.itemId}>
                  <label
                    className={cn(
                      "grid h-16 cursor-pointer grid-cols-[15px_32px_16px_minmax(0,1fr)_136px] items-center gap-2.5 border-b border-[#141417] px-4 transition-opacity hover:bg-[#0F0F12]",
                      !on && "opacity-[0.72]",
                      disabled && "cursor-default"
                    )}
                    title={locked && canManageAgenda ? AGENDA_LOCKED_TEXT : `AB#${item.itemId} · ${item.title}`}
                  >
                    <input
                      type="checkbox"
                      className="h-[15px] w-[15px] accent-primary"
                      aria-label={`Add AB#${item.itemId} to the agenda`}
                      checked={on}
                      disabled={disabled}
                      onChange={() => {
                        toggle(item.itemId);
                      }}
                    />
                    <span className={cn("font-display text-xs font-semibold", TOOLBOX_TEXT.faint)}>#{rank}</span>
                    <KindIcon kind={item.kind === "bug" ? "bug" : "story"} />
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-[13px]">
                        {pendingId === item.itemId ? "Saving… " : ""}
                        {item.title}
                      </span>
                      {item.snippet && (
                        <span className="truncate text-xs text-[#A1A1AA]" title={item.snippet} data-testid="session-row-snippet">
                          {item.snippet}
                        </span>
                      )}
                      <span className="truncate text-xs text-muted-foreground" data-testid="session-row-who-date">
                        {whoDate(item)}
                      </span>
                    </span>
                    <span className="justify-self-end">
                      <BriefChip
                        state={briefState}
                        commentStatus={item.aiSuggestion?.commentStatus ?? null}
                        testId="queue-brief"
                      />
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {more > 0 && (
          <button
            type="button"
            className={cn("px-4 py-2.5 text-left text-xs hover:text-foreground", TOOLBOX_TEXT.faint)}
            data-testid="queue-more"
            onClick={() => {
              setShowAll(true);
            }}
          >
            + {more} more ready to groom, further down the backlog
          </button>
        )}
        {atCap && (
          <p className="px-4 py-2 text-[13px] text-amber-400" data-testid="agenda-cap">
            An agenda holds at most {MAX_GROOMING_BATCH_ITEMS} items. Untick one to add another.
          </p>
        )}
        <span className="flex-1" />
        <div className={cn("flex items-center gap-2 border-t border-[#1F1F23] px-4 py-2.5 text-[13px]", TOOLBOX_TEXT.faint)}>
          <Info className="h-[13px] w-[13px] flex-none" aria-hidden="true" />
          Items that need information stay out of the session. Their authors get questions from the item page.
        </div>
      </section>

      {/* The agenda */}
      <section
        className="glass-card flex min-h-[320px] w-full flex-none flex-col overflow-hidden rounded-[10px] lg:w-[440px]"
        aria-labelledby="agenda-heading"
        data-testid="agenda-panel"
      >
        <div className="flex items-center gap-2 border-b border-[#1F1F23] px-4 py-3">
          <h2 id="agenda-heading" className="flex-1 font-display text-sm font-semibold" data-testid="agenda-heading">
            {entries.length === 0 ? "Agenda" : `Agenda · ${String(entries.length)} ${entries.length === 1 ? "item" : "items"}`}
          </h2>
          <span className="text-xs text-muted-foreground">in the order you&apos;ll discuss them</span>
        </div>
        {entries.length === 0 ? (
          <div className={cn("flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center", TOOLBOX_TEXT.faint)} data-testid="agenda-empty">
            <CalendarPlus className="h-[22px] w-[22px]" aria-hidden="true" />
            <span>
              {canManageAgenda
                ? "Tick items on the left to put them on the agenda, or press A on a backlog item."
                : "No items on the agenda yet."}
            </span>
          </div>
        ) : (
          <ol className="min-h-0 overflow-auto" aria-label="Agenda items">
            {entries.map((entry, index) => {
              return (
                <li
                  key={entry.itemId}
                  className={cn(
                    "grid min-h-[46px] items-center gap-2.5 border-b border-[#141417] px-4 motion-safe:animate-slide-up",
                    editable ? "grid-cols-[24px_minmax(0,1fr)_136px_auto_20px]" : "grid-cols-[24px_minmax(0,1fr)_136px_20px]"
                  )}
                  data-testid="agenda-entry"
                  data-item-id={entry.itemId}
                >
                  <span className={cn("font-display text-[15px] font-semibold", TOOLBOX_TEXT.faint)}>{index + 1}</span>
                  <span className="flex min-w-0 flex-col gap-0.5 py-1.5">
                    <span className="truncate text-[13px]" title={`AB#${entry.itemId} · ${entry.title}`}>
                      {entry.title}
                    </span>
                    {entry.whoDate && (
                      <span className="truncate text-xs text-muted-foreground" data-testid="agenda-row-who-date">
                        {entry.whoDate}
                      </span>
                    )}
                  </span>
                  <span className="justify-self-end">
                    {/* E284: an item waiting or running in this computer's run queue shows its place instead. */}
                    <AgendaQueueOr project={project} itemId={entry.itemId}>
                      <BriefChip state={entry.brief} commentStatus={entry.commentStatus} testId="agenda-brief" />
                    </AgendaQueueOr>
                  </span>
                  {editable && (
                    <SessionPicker
                      project={project}
                      currentBatchId={agenda.id}
                      label="Move"
                      testId="agenda-move"
                      disabled={busy}
                      onPick={(date) => {
                        moveItem(entry.itemId, date);
                      }}
                    />
                  )}
                  {editable ? (
                    <button
                      type="button"
                      className={cn("flex h-5 w-5 items-center justify-center rounded hover:bg-[#1A1A1E] hover:text-foreground disabled:opacity-40", TOOLBOX_TEXT.faint)}
                      aria-label={`Remove AB#${entry.itemId} from the agenda`}
                      disabled={busy}
                      onClick={() => {
                        removeItem(entry.itemId);
                      }}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : (
                    <span />
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <span className="flex-1" />
        <div className={cn("flex flex-col gap-1.5 border-t border-[#1F1F23] px-4 py-3 text-[13px] leading-relaxed", TOOLBOX_TEXT.faint)} data-testid="agenda-notes">
          {locked && (
            <span className="text-foreground" data-testid="agenda-locked">
              {AGENDA_LOCKED_TEXT}
            </span>
          )}
          {canPrepare && typicalItemSeconds !== null && typicalItemSeconds > 0 && (
            <span data-testid="agenda-timing-note">{prepareTimingNote(typicalItemSeconds)}</span>
          )}
          {canPrepare ? (
            <AgendaPrepareNote />
          ) : canManageAgenda ? (
            <span>A Project Manager prepares the briefs. You can build the agenda and share it.</span>
          ) : (
            <span data-testid="agenda-read-only">A Project Manager builds the agenda and prepares the briefs. You can read and share it.</span>
          )}
        </div>
        {error && (
          <p className="border-t border-[#1F1F23] px-4 py-2 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </section>
    </div>
  );
}
